
import {
    ParsingErrorKind, IParsingError, IObjectSchema, IExpressionResolution, ResolutionErrorKind,
    failedExpressionResolution, successfulExpressionResolution, IResolutionError, IExpressionResolver, IExpressionProperty, getAcceptsByConfig, suggestValidType
} from 'app/model/expressions/expression-resolution';
import { Activity, Link } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { FunctionArgumentParser, IArgument } from 'app/model/expressions/function-argument-parser';
import { ExpressionType, ExpressionCategory, ExpressionFunction, ScalarExpression } from 'app/model/expressions/expression';
import { format } from 'app/shared/utils/string.util';
import { Dataset } from 'app/model/dataset/dataset';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { IAttribute, IModel } from 'app/model/base/annotation';
import { ForEachActivity } from '../pipeline/foreach.activity';
import { UntilActivity } from '../pipeline/until.activity';
import { IfConditionActivity } from '../pipeline/if-condition.activity';
import { WaitActivity } from '../pipeline/wait.activity';
import { SetVariableActivity } from '../pipeline/variables.activity';
import { AppendVariableActivity } from '../pipeline/variable-append.activity';
import { LinkedService } from '../linkedservice/linkedservice';
import { LookupActivity } from '../pipeline/lookup.activity';
import { GetMetadataActivity } from '../pipeline/get-metadata.activity';
import { Metamodel } from '../base/model';
import { ValidationActivity } from '../pipeline/validation.activity';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { ExecutePipelineActivity, ExecutePipelineActivityDuplicate, InvokePipelineConnectionType } from '../pipeline/execute-pipeline.activity';

export function buildExpressionErrorMessage(expressionResolutionError: IResolutionError): string {
    let detailedError = getResolutionErrorMessage(expressionResolutionError);

    if (expressionResolutionError.parsingError) {
        detailedError += `: ${getParsingErrorMessage(expressionResolutionError.parsingError.error)}`;
    }

    return detailedError;
}

export function getResolutionErrorMessage(resolutionError: IResolutionError): string {
    let errorMessage = '';

    switch (resolutionError.kind) {
        case ResolutionErrorKind.ParameterNotFound:
            errorMessage = format(stringResources.Expression_resolution_ParameterNotFound, resolutionError.details.parameterName, resolutionError.details.conceptName);
            break;
        case ResolutionErrorKind.ParameterNotSpecified:
            errorMessage = stringResources.Expression_resolution_ParameterNotSpecified;
            break;
        case ResolutionErrorKind.TypeMismatch:
            errorMessage = buildTypeMismatchErrorMsg(resolutionError);
            break;
        case ResolutionErrorKind.Unrecognized:
            errorMessage = stringResources.Expression_resolution_Unrecognized;

            if (resolutionError.details) {
                errorMessage += (`: ${resolutionError.details}`);
            }
            break;
        case ResolutionErrorKind.UnrecognizedType:
            errorMessage = format(
                stringResources.Expression_resolution_UnrecognizedType,
                resolutionError.details.source,
                resolutionError.details.expressionType);
            break;
        case ResolutionErrorKind.InvalidSyntax:
            errorMessage = stringResources.Expression_resolution_InvalidSyntax;
            break;
        case ResolutionErrorKind.FunctionNotFound:
            errorMessage = format(stringResources.Expression_resolution_FunctionNotFound, resolutionError.details.functionName);
            break;
        case ResolutionErrorKind.SystemVariableNotFound:
            errorMessage = format(stringResources.Expression_resolution_SystemVariableNotFound, resolutionError.details.systemVariableName);
            break;
        case ResolutionErrorKind.SystemVariableNestedPipelineWarning:
            errorMessage = format(stringResources.Expression_resolution_SystemVariableNestedPipelineWarning, resolutionError.details.systemVariableName);
            break;
        case ResolutionErrorKind.TriggerEventWarning:
            errorMessage = format(stringResources.Expression_resolution_TriggerEventWarning, resolutionError.details.triggerParameterName);
            break;
        case ResolutionErrorKind.InvalidArgumentCount:
            errorMessage = format(stringResources.Expression_resolution_InvalidArgumentCount, resolutionError.details.func, resolutionError.details.argumentCount);
            break;
        case ResolutionErrorKind.NoMatchingArguments:
            errorMessage = format(stringResources.Expression_resolution_NoMatchingArguments, resolutionError.details.func);

            if (resolutionError.details.arguments) {
                errorMessage = `${errorMessage}: (${resolutionError.details.arguments})`;
            }
            break;
        case ResolutionErrorKind.ExpressionContainsWhiteSpace:
            errorMessage = stringResources.Expression_resolution_ExpressionContainsWhiteSpace;
            break;
        case ResolutionErrorKind.EmptyExpression:
            errorMessage = stringResources.Expression_resolution_EmptyExpression;
            break;
        case ResolutionErrorKind.SingleStringLiteralError:
            errorMessage = stringResources.Expression_resolution_SingleStringLiteralError;
            break;
        case ResolutionErrorKind.MissingActivityName:
            errorMessage = stringResources.Expression_resolution_MissingActivityName;
            break;
        case ResolutionErrorKind.MissingVariableNameResolutionError:
            errorMessage = stringResources.Expression_resolution_MissingVariableName;
            break;
        case ResolutionErrorKind.PropertyNotFound:
            errorMessage = format(stringResources.Expression_resolution_PropertyNotFound, resolutionError.details.propertyName);
            break;
        case ResolutionErrorKind.ExcesssNestedProperties:
            errorMessage = format(stringResources.Expression_resolution_ExcesssNestedProperties, resolutionError.details.propertyName);
            break;
        case ResolutionErrorKind.UseBuiltInArithmetic:
            errorMessage = stringResources.Expression_resolution_UseBuiltInArithmetic;
            break;
        case ResolutionErrorKind.ActivityNotReferencable:
            errorMessage = format(stringResources.Expression_resolution_ActivityNotReferencable, resolutionError.details.entityName);
            break;
        case ResolutionErrorKind.ActivityNoOutput:
            errorMessage = format(stringResources.Expression_resolution_ActivityFoundNoOutput, resolutionError.details.entityName);
            break;
        case ResolutionErrorKind.VariableNotReferencable:
            errorMessage = format(stringResources.Expression_resolution_VariableNotReferencable, resolutionError.details.entityName);
            break;
        case ResolutionErrorKind.SelfReferencingVariable:
            errorMessage = format(stringResources.Expression_resolution_SelfReferencingError);
            break;
        case ResolutionErrorKind.ItemsSyntaxNotApplicable:
            errorMessage = stringResources.Expression_resolution_ItemsSyntaxNotApplicable;
            break;
        case ResolutionErrorKind.EmptyInterpolationExpression:
            errorMessage = stringResources.Expression_resolution_EmptyInterpolationExpression;
            break;
        case ResolutionErrorKind.NotAnArray:
            errorMessage = format(stringResources.Expression_resolution_NotAnArray, resolutionError.details.propertyName);
            break;
        case ResolutionErrorKind.ExpressionNotApplicable:
            errorMessage = stringResources.Expression_resolution_ExpressionNotApplicable;
            break;
        case ResolutionErrorKind.ParameterInaccessible:
            errorMessage = stringResources.Expression_resolution_ExpressionNotApplicable;

            if (resolutionError.details && resolutionError.details.sourceModel && resolutionError.details.target) {
                const sourceModel = resolutionError.details.sourceModel as IModel;
                const target = resolutionError.details.target as Owned;

                if (sourceModel && target && target.concept) {
                    if (sourceModel === Dataset.MODEL && target.concept instanceof Pipeline) {
                        errorMessage = stringResources.Expression_resolution_DatasetParameterInaccessible;
                    } else if (sourceModel === Pipeline.MODEL && target.concept instanceof Dataset) {
                        errorMessage = stringResources.Expression_resolution_PipelineParameterInaccessible;
                    } else if (sourceModel === Pipeline.MODEL && target.concept instanceof LinkedService) {
                        errorMessage = stringResources.Expression_resolution_LSPipelineParameterInaccessible;
                    }
                }
            }

            break;
        case ResolutionErrorKind.VariablesUnsupported:
            errorMessage = stringResources.Expression_resolution_ExpressionNotApplicable;

            if (resolutionError.details && resolutionError.details.target) {
                const target = resolutionError.details.target as Owned;
                if (target.concept instanceof Dataset) {
                    errorMessage = stringResources.Expression_resolution_VariablesInaccessible;
                }
            }
            break;
        default:
            errorMessage = ResolutionErrorKind[Number(resolutionError.kind)];
            break;
    }

    return errorMessage;
}

export function getParsingErrorMessage(parsingError: ParsingErrorKind): string {
    let errorMessage = '';

    switch (parsingError) {
        case ParsingErrorKind.ExtraTrailingComma:
            errorMessage = stringResources.Expression_resolution_ExtraTrailingComma;
            break;
        case ParsingErrorKind.ExtraTrailingSpace:
            errorMessage = stringResources.Expression_resolution_ExtraTrailingSpace;
            break;
        case ParsingErrorKind.MissingClosingBracket:
            errorMessage = stringResources.Expression_resolution_MissingClosingBracket;
            break;
        case ParsingErrorKind.MissingClosingQuote:
            errorMessage = stringResources.Expression_resolution_MissingClosingQuote;
            break;
        case ParsingErrorKind.MissingClosingBrace:
            errorMessage = stringResources.Expression_resolution_MissingClosingBrace;
            break;
        case ParsingErrorKind.InvalidJsonSyntax:
            errorMessage = stringResources.InvalidJsonSyntaxLabel;
            break;
        case ParsingErrorKind.MissingComma:
            errorMessage = stringResources.Expression_resolution_MissingComma;
            break;
        case ParsingErrorKind.UnrecognizedToken:
            errorMessage = stringResources.Expression_resolution_UnrecognizedToken;
            break;
        case ParsingErrorKind.ExtraTrailingDot:
            errorMessage = stringResources.Expression_resolution_ExtraTrailingDot;
            break;
        case ParsingErrorKind.MissingDot:
            errorMessage = stringResources.Expression_resolution_MissingDot;
            break;
        case ParsingErrorKind.MissingEnclosingParanthesis:
            errorMessage = stringResources.Expression_resolution_MissingEnclosingParenthesis;
            break;
        case ParsingErrorKind.ExtraEnclosingParanthesis:
            errorMessage = stringResources.Expression_resolution_ExtraEnclosingParenthesis;
            break;
        case ParsingErrorKind.OpeningParanthesisExpected:
            errorMessage = stringResources.Expression_resolution_OpeningParenthesisExpected;
            break;
        case ParsingErrorKind.ExtraClosingBracket:
            errorMessage = stringResources.Expression_resolution_ExtraClosingBracket;
            break;
        case ParsingErrorKind.InvalidArrayProperty:
            errorMessage = stringResources.Expression_resolution_InvalidArrayProperty;
            break;
        case ParsingErrorKind.NullSafeOperatorIncomplete:
            errorMessage = stringResources.Expression_resolution_NullSafeOperatorIncomplete;
            break;
        default:
            errorMessage = stringResources.Expression_resolution_GeneralParsingError;
            break;
    }

    return errorMessage;
}

/**
 * Gets a stack of nested properties in a string. e.g. object.prop1.prop2.
 * @param syntax The syntax to process.
 * @param offset An optional offset to add to error indexes.
 */
export function getPropertyStack(syntax: string, offset: number = 0): IExpressionProperty[] {
    const propertyNestings: IExpressionProperty[] = [];

    let i = 0;
    let token = '';
    let separator: '.' | '?.' | 'none' = '.';

    // special case accessing array property of expression
    // Note: unbalanced brackets (e.g. '[[]') is not checked here.
    // Instead, the method that evaluates subscript argument type/value
    // will catch the unbalanced bracket count and provide a better
    // error message.
    if (syntax.charAt(0) === '[') {
        // Find all the array arguments. Start of an array argument is '['.
        while (i < syntax.length && syntax.charAt(i) === '[') {
            // Loop until the end of the array argument (']')
            while (i < syntax.length && syntax.charAt(i) !== ']') {
                i++;
            }

            // Reached the end without finding the closing bracket
            if (i >= syntax.length) {
                throw {
                    error: ParsingErrorKind.InvalidArrayProperty,
                    startIndex: i + token.lastIndexOf(']') + 1 + offset
                };
            }
            i++;
        }

        propertyNestings.push({ property: syntax.substring(0, i), separator: 'none' });

    }

    while (i < syntax.length) {
        switch (syntax.charAt(i)) {
            case '.':
                // regular dot operator
                separator = '.';
                i++;
                break;
            case '?':
                if (i + 1 < syntax.length && syntax.charAt(i + 1) === '.') {
                    // null safe dot operator ?.
                    separator = '?.';
                    i += 2;
                } else if (i + 1 < syntax.length && syntax.charAt(i + 1) === '[') {
                    separator = 'none';
                    i++;
                } else {
                    throw {
                        error: ParsingErrorKind.NullSafeOperatorIncomplete,
                        startIndex: i + offset
                    };
                }
                break;
            default:
                // each property must be preceded by a dot
                throw {
                    error: ParsingErrorKind.MissingDot,
                    startIndex: i + offset
                };
        }

        token = getNextPropertyToken(syntax, i, offset);
        if (!token) {
            // we have a trailing dot
            throw {
                error: ParsingErrorKind.ExtraTrailingDot,
                startIndex: i + offset
            };
        }

        propertyNestings.push({ property: token, separator });

        // advance the index beyond the current token
        i += token.length;
    }

    return propertyNestings;
}

export function isArraySyntax(propertyName: string): boolean {
    // if the property name has [] then it is considered an array
    return /\[[\S ]*\]$/.test(propertyName);
}

/**
 * List of activities that have no output.
 */
const noOutputActivities = [
    ForEachActivity,
    WaitActivity,
    UntilActivity,
    IfConditionActivity,
    SetVariableActivity,
    AppendVariableActivity
];

export function getScalarOutputExpressionsForLookupActivities(ancestry: Activity[]): ScalarExpression[] {
    const expressions: ScalarExpression[] = [];
    ancestry?.forEach((activity) => {
        const activityType = Metamodel.modelOf(activity).name;
        if (activity instanceof ValidationActivity) {
            expressions.push(new ScalarExpression(
                ExpressionCategory.ActivityOutput,
                `${activity.name} exists`,
                `@activity('${activity.name}').output.exists`,
                stringResources.ExpressionBuilder_GetMetaActivityOutputExistsDesc,
                'Bool',
                undefined,
                activityType
            ));
        } else if (activity instanceof LookupActivity) {
            // LookupActivity.firstRowOnly is a tri-state property and by default it would be activated even undefined
            if (activity.firstRowOnly !== false) {
                expressions.push(new ScalarExpression(
                    ExpressionCategory.ActivityOutput,
                    `${activity.name} first row`,
                    `@activity('${activity.name}').output.firstRow`,
                    stringResources.ExpressionBuilder_LookUpActivityOutputFirstRowDesc,
                    'Object',
                    undefined,
                    activityType
                ));
            } else {
                expressions.push(new ScalarExpression(
                    ExpressionCategory.ActivityOutput,
                    `${activity.name} count`,
                    `@activity('${activity.name}').output.count`,
                    stringResources.ExpressionBuilder_LookUpActivityOutputCountDesc,
                    'Int',
                    undefined,
                    activityType
                ));
                expressions.push(
                    new ScalarExpression(
                        ExpressionCategory.ActivityOutput,
                        `${activity.name} value array`,
                        `@activity('${activity.name}').output.value`,
                        stringResources.ExpressionBuilder_LookUpActivityOutputValueDesc,
                        'Array',
                        undefined,
                        activityType
                    )
                );
            }
        } else if (activity instanceof GetMetadataActivity) {
            const curGetMetaDataActivityFieldNameSet = activity.getFieldNameSet();
            for (const oneField of curGetMetaDataActivityFieldNameSet) {
                switch (oneField) {
                    case 'structure':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} structure`,
                                `@activity('${activity.name}').output.structure`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputStructureDesc,
                                'Array',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    case 'columnCount':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} columnCount`,
                                `@activity('${activity.name}').output.columnCount`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputColumnCountDesc,
                                'Int',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    case 'exists':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} exists`,
                                `@activity('${activity.name}').output.exists`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputExistsDesc,
                                'Bool',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    case 'itemName':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} itemName`,
                                `@activity('${activity.name}').output.itemName`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputItemNameDesc,
                                'String',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    case 'itemType':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} itemType`,
                                `@activity('${activity.name}').output.itemType`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputItemTypeDesc,
                                'String',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    case 'lastModified':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} lastModified`,
                                `@activity('${activity.name}').output.lastModified`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputLastModifiedDesc,
                                'String',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    case 'childItems':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} childItems`,
                                `@activity('${activity.name}').output.childItems`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputChildItemsDesc,
                                'Array',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    case 'size':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} size`,
                                `@activity('${activity.name}').output.size`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputSizeDesc,
                                'Int',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    case 'contentMD5':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} contentMD5`,
                                `@activity('${activity.name}').output.contentMD5`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputContentMD5Desc,
                                'String',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    case 'created':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} created`,
                                `@activity('${activity.name}').output.created`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputCreatedDesc,
                                'String',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    case 'partitionIndexes':
                        expressions.push(
                            new ScalarExpression(
                                ExpressionCategory.ActivityOutput,
                                `${activity.name} partitionIndexes`,
                                `@activity('${activity.name}').output.partitionIndexes`,
                                stringResources.ExpressionBuilder_GetMetaActivityOutputPartitionIndexesDesc,
                                'Any',
                                undefined,
                                activityType
                            )
                        );
                        break;
                    default:
                        // noop
                        break;
                }
            }
        } else if (activity instanceof ExecutePipelineActivity) {
            if (activity instanceof ExecutePipelineActivityDuplicate && activity.invokePipelineType === InvokePipelineConnectionType.FABRIC) {
                expressions.push(new ScalarExpression(
                    ExpressionCategory.ActivityOutput,
                    `${activity.name}`,
                    `@activity('${activity.name}').output.properties.returnValue`,
                    `${activity.name} ${stringResources.PipelineOutput.toLowerCase()}`,
                    'Object'
                ));
            } else {
                expressions.push(new ScalarExpression(
                    ExpressionCategory.ActivityOutput,
                    `${activity.name}`,
                    `@activity('${activity.name}').output.pipelineReturnValue`,
                    `${activity.name} ${stringResources.PipelineOutput.toLowerCase()}`,
                    'Object'
                ));
            }
        }
    });

    return expressions;
}

export function findLastActivityInLineage(ancestors: Activity[], activityType: string): Activity {
    let activity: Activity = undefined;
    for (let i = ancestors.length - 1; i >= 0; i--) {
      const model = Metamodel.modelOf(ancestors[i]);
      if (model.name.toLowerCase() === activityType.toLowerCase()) {
        activity = ancestors[i]
        break;
      }
    }

    return activity;
  }


export function getActivityAncestry(activity: Activity): Activity[] {
    return getActivityAncestryWithExclusions(activity).activities;
}

export function getActivityAncestryWithExclusions(activity: Activity): { activities: Activity[], exclusions: Activity[] } {
    const ancestry = new Set<Activity>();
    const processingQueue: Link[] = [];
    let currentOwned: Owned = activity;

    function checkChildActivities(currentActivity: Activity): void {
        if (currentActivity instanceof ForEachActivity || currentActivity instanceof UntilActivity) {
            if (currentActivity.pipeline && currentActivity.pipeline.activities) {
                currentActivity.pipeline.activities.forEach((childActivity: Activity) => {
                    ancestry.add(childActivity);
                    checkChildActivities(childActivity);
                });
            }
        } else if (currentActivity instanceof IfConditionActivity) {
            if (currentActivity.ifFalseActivityPipeline && currentActivity.ifFalseActivityPipeline.activities) {
                currentActivity.ifFalseActivityPipeline.activities.forEach((childActivity: Activity) => {
                    ancestry.add(childActivity);
                    checkChildActivities(childActivity);
                });
            }
            if (currentActivity.ifTrueActivityPipeline && currentActivity.ifTrueActivityPipeline.activities) {
                currentActivity.ifTrueActivityPipeline.activities.forEach((childActivity: Activity) => {
                    ancestry.add(childActivity);
                    checkChildActivities(childActivity);
                });
            }
        }
    }

    // add direct incoming links into the activity as well as its parent activities in case of nesting (if, foreach,...)
    // we prioritize current level ancestors in case of name clashes across nesting levels (we add them first to the ancestry list)
    while (currentOwned) {
        if (currentOwned instanceof Activity && currentOwned.fromLinks) {
            // add the from link pointing to the activity
            processingQueue.push(...currentOwned.fromLinks);
        }

        while (processingQueue.length > 0) {
            // take out the first link
            const link = processingQueue.shift();

            // ensure the from activity was not processed before and that it is not the activity we are trying to find the ancestry for to avoid dead locks
            if (!ancestry.has(link.fromActivity) && link.fromActivity !== activity) {
                ancestry.add(link.fromActivity);

                if (link.fromActivity.fromLinks) {
                    processingQueue.push(...link.fromActivity.fromLinks);
                }
            }

            checkChildActivities(link.fromActivity);
        }

        // check the owner
        currentOwned = currentOwned.owner;
    }

    checkChildActivities(activity);

    const activities = [...ancestry].filter((activity1) => !noOutputActivities.find((activityType) => activity1 instanceof activityType));
    const exclusions = [...ancestry].filter((activity1) => noOutputActivities.find((activityType) => activity1 instanceof activityType));

    // filter out forEach activities, as they don't have output
    return { activities, exclusions };
}

export function matchPropertyStackWithSchema(
    expression: string,
    propertyStack: Array<{ property: string, separator: string }>,
    schema: IObjectSchema,
    offset: number,
    expressionResolverFunc: (expr: string) => IExpressionResolution): IExpressionResolution {

    let propertySchema: IObjectSchema = schema;
    let objectIndex;
    let startIndex = 0;
    let genericObjectDetected = false;

    // process the nested properties one by one
    for (objectIndex = 0; objectIndex < propertyStack.length; ++objectIndex) {
        startIndex += (propertyStack[objectIndex].separator !== 'none') ? propertyStack[objectIndex].separator.length : 0;
        const propertyName = propertyStack[objectIndex].property;

        if (isArraySyntax(propertyName)) {
            // extract the array's name and index
            const openingBracketIndex = propertyName.indexOf('[');
            const arrayName = propertyName.substring(0, openingBracketIndex);
            const arrayIndexSyntax = propertyName.substring(openingBracketIndex);

            if (propertySchema.schema) {
                propertySchema = propertySchema.schema[arrayName];

                if (!propertySchema) {
                    return failedExpressionResolution(expression, {
                        kind: ResolutionErrorKind.PropertyNotFound,
                        startIndex: offset + startIndex,
                        endIndex: offset + startIndex + propertyName.length,
                        details: { propertyName }
                    });
                }
            } else if (propertySchema.expressionType === 'Object') {
                if (genericObjectDetected) {
                    // this is a property within an object which did not have a schema, it could be anything
                    propertySchema = { expressionType: 'Any' };
                }

                genericObjectDetected = true;
            }

            // only arrays and any support the array syntax
            if (propertySchema.expressionType !== 'Array' && propertySchema.expressionType !== 'Any' && !genericObjectDetected) {
                return failedExpressionResolution(expression, {
                    kind: ResolutionErrorKind.NotAnArray,
                    startIndex: offset + startIndex,
                    endIndex: offset + startIndex + propertyName.length,
                    details: { propertyName: arrayName }
                });
            }

            try {
                resolveArrayIndexArgument(offset + startIndex + openingBracketIndex, arrayIndexSyntax, expressionResolverFunc);
            } catch (error) {
                return error as IExpressionResolution;
            }

            propertySchema = propertySchema.schema ? propertySchema.schema['element'] : { expressionType: 'Any' };
        } else {
            if (propertySchema.schema) {
                propertySchema = propertySchema.schema[propertyName];

                if (!propertySchema) {
                    return failedExpressionResolution(expression, {
                        kind: ResolutionErrorKind.PropertyNotFound,
                        startIndex: offset + startIndex,
                        endIndex: offset + startIndex + propertyName.length,
                        details: { propertyName }
                    });
                }
            } else if (propertySchema.expressionType === 'Object') {
                if (genericObjectDetected) {
                    // this is a property within an object which did not have a schema, it could be anything
                    propertySchema = { expressionType: 'Any' };
                }

                genericObjectDetected = true;
            }

            // regular property, no array subscript used
            if (propertySchema.expressionType !== 'Object' && propertySchema.expressionType !== 'Any') {
                // this is a terminal property, i.e. does not make sense to use the dot operator here
                if (objectIndex + 1 < propertyStack.length) {
                    // there are excess properties specified
                    return failedExpressionResolution(expression, {
                        kind: ResolutionErrorKind.ExcesssNestedProperties,
                        startIndex: offset + startIndex + propertyName.length,
                        details: { propertyName }
                    });
                }
            }
        }

        startIndex += propertyName.length;
        genericObjectDetected = propertySchema.expressionType === 'Object' && !propertySchema.schema;
    }

    if (propertySchema.expressionType === 'Array' && !propertySchema.subType) {
        propertySchema.subType = 'Any';
    }

    return successfulExpressionResolution(expression, new ScalarExpression(
        ExpressionCategory.Parameter,
        propertyStack && propertyStack.length > 0 ? propertyStack[propertyStack.length - 1].property : '',
        expression, '', propertySchema.expressionType, propertySchema.subType));
}

/**
 * Finds the index of the given pattern within an expression given where to start from. Ensures the pattern is not a part of a string literal.
 * @param expression The expression to scan.
 * @param pattern  The pattern to look for.
 * @param startIndex The starting point of the search.
 */
export function findPattern(expression: string, pattern: string, initialStartIndex: number = 0): number {
    let startOfStringLiteral = -1;
    for (let i = initialStartIndex; i < expression.length; ++i) {
        switch (expression.charAt(i)) {
            case '\'':
                // In expressions, a single quote character literal is escaped by an adjacent single quote.
                const isEscaped = i > initialStartIndex && expression.charAt(i + 1) === '\'';
                if (!isEscaped) {
                    const isStartingStringLiteral = startOfStringLiteral === -1;
                    startOfStringLiteral = isStartingStringLiteral ? i + 1 : -1;
                } else {
                    // Skip adjacent single quote.
                    i++;
                }
                break;
            default:
                if (startOfStringLiteral === -1 && pattern === expression.substr(i, pattern.length)) {
                    return i;
                }
                break;
        }
    }
    // If a string literal is not closed, then we ignore the string literal error, and check that the pattern is found or not regardless if it's inside the string literal.
    if (startOfStringLiteral !== -1) {
        return findPattern(expression, pattern, startOfStringLiteral);
    }
    return -1;
}

export function buildSubExpressionResolver(entity: Owned, expressionResolversCallback: () => IExpressionResolver[]): (expr: string) => IExpressionResolution {
    return (expr: string): IExpressionResolution => {
        for (const resolver of expressionResolversCallback()) {
            if (resolver.isOfType(expr, true)) {
                const applicability = resolver.checkApplicability(entity);

                return applicability.isApplicable
                    ? resolver.resolve(expr, true)
                    : failedExpressionResolution(expr, applicability.error || { kind: ResolutionErrorKind.ExpressionNotApplicable }, undefined, undefined, applicability.treatAsWarning);
            }
        }

        const endIndex = expr ? expr.length - 1 : undefined;
        return failedExpressionResolution(expr, { kind: ResolutionErrorKind.Unrecognized, details: expr, startIndex: 0, endIndex });
    };
}

function resolveArrayIndexArgument(offset: number, arrayIndexSyntax: string, expressionResolverFunc: (expr: string) => IExpressionResolution): void {
    let arrayArgs: IArgument[];

    try {
        arrayArgs = new FunctionArgumentParser(offset, expressionResolverFunc).parse(arrayIndexSyntax, true);
    } catch (error) {
        const parsingError = error as IParsingError;

        const innerError = getInnerError(parsingError);

        throw innerError ||
        failedExpressionResolution(arrayIndexSyntax, {
            kind: ResolutionErrorKind.InvalidSyntax,
            parsingError
        });
    }

    if (!arrayArgs || arrayArgs.length <= 0) {
        throw failedExpressionResolution(arrayIndexSyntax, {
            kind: ResolutionErrorKind.InvalidArgumentCount,
            startIndex: offset,
            endIndex: offset + arrayIndexSyntax.length,
            details: { func: 'Array', argumentCount: arrayArgs.length }
        });
    }

    for (const arg of arrayArgs) {
        if (!isValidArrayArg(arg)) {
            throw failedExpressionResolution(arrayIndexSyntax, {
                kind: ResolutionErrorKind.NoMatchingArguments,
                startIndex: offset,
                endIndex: offset + arrayIndexSyntax.length,
                details: { func: stringResources.Expression_resolution_Array_Subscript }
            });
        }
    }
}

function isValidArrayArg(arg: IArgument): boolean {
    if (arg.kind === 'Array' && arg.elements && arg.elements.length === 1) {
        if (arg.elements[0].kind === 'IntegerLiteral') {
            if (Number.isInteger(Number(arg.elements[0].stringValue))) {
                // integer literal is expected inside the array subscript operator
                return true;
            }
        }

        if (arg.elements[0].kind === 'StringLiteral') {
            // can access array properties using string literals
            return true;
        }

        if (arg.elements[0].kind === 'Expression' && arg.elements[0].expressionResolution && arg.elements[0].expressionResolution.expression) {
            let expressionReturnType: ExpressionType;

            if (arg.elements[0].expressionResolution.expression.category === ExpressionCategory.Function) {
                const functionExpression: ExpressionFunction = arg.elements[0].expressionResolution.expression as ExpressionFunction;
                expressionReturnType = functionExpression.specification.returnType;
            } else {
                const scalarExpression: ScalarExpression = arg.elements[0].expressionResolution.expression as ScalarExpression;
                expressionReturnType = scalarExpression.returnType;
            }

            if (expressionReturnType === 'Int' || expressionReturnType === 'Any' || expressionReturnType === 'String') {
                // expressions returning and INT or ANY are compatable with array indexes
                return true;
            }
        }
    }

    return false;
}

function getNextPropertyToken(syntax: string, start: number, offset: number): string {
    const separators = ['.', '?'];
    let bracketCounter = 0;
    let lastClosingBracketIndex = -1;
    let nextSeparatorIndex = -1;

    for (let i = start; i < syntax.length; i++) {
        const char = syntax[i];

        // Ignore everything between quotes.
        if (char === '\'') {
            const closingSingleQuoteIndex = syntax.indexOf('\'', i + 1);

            if (closingSingleQuoteIndex < 0) {
                throw {
                    error: ParsingErrorKind.MissingClosingQuote,
                    startIndex: i + offset
                };
            } else {
                // Update index to skip string literal chars.
                i = closingSingleQuoteIndex;
            }
        } else if (char === '[') {
            // we are inside a bracket which could contain an expression which has dots
            // we do not want to split the expression
            bracketCounter++;
        } else if (char === ']') {
            // bracket expression closed
            bracketCounter--;

            // update position for closing bracket.
            lastClosingBracketIndex = i;
        } else if (bracketCounter === 0 && separators.includes(char)) {
            // Found a separator char that is not between brackets.
            nextSeparatorIndex = i;
            break;
        }
    }

    if (bracketCounter === 0) {
        // keep the closing bracket if the property uses array syntax.
        const tokenEndIndex = Math.max(nextSeparatorIndex, lastClosingBracketIndex + 1);

        return tokenEndIndex < start
            // ignore separator char which precedes the start index.
            ? syntax.substring(start)
            : syntax.substring(start, tokenEndIndex);
    } else if (bracketCounter < 0) {
        // too many closing brackets
        throw {
            error: ParsingErrorKind.ExtraClosingBracket,
            startIndex: lastClosingBracketIndex + offset
        };
    } else {
        // too many opening brackets
        throw {
            error: ParsingErrorKind.InvalidArrayProperty,
            startIndex: syntax.indexOf('[', start) + offset
        };
    }
}

export function getInnerError(parsingError: IParsingError): IExpressionResolution {
    if (parsingError && parsingError.resolution) {
        const resolutionError = parsingError.resolution.error as IResolutionError;

        if (resolutionError) {
            if (resolutionError.parsingError) {
                // shift the inner parsing error indexes by the parent parsing error indexes.
                if (parsingError.endIndex !== undefined) {
                    resolutionError.parsingError.endIndex += parsingError.startIndex;
                }

                resolutionError.parsingError.startIndex += parsingError.startIndex;
            } else {
                // shift the inner resolution error indexes by the parent resolution error indexes.
                if (resolutionError.startIndex !== undefined) {
                    resolutionError.startIndex += parsingError.startIndex;
                }

                if (resolutionError.endIndex !== undefined) {
                    resolutionError.endIndex += parsingError.startIndex;
                }
            }
        }

        return parsingError.resolution;
    }

    return undefined;
}
function buildTypeMismatchErrorMsg(resolutionError: IResolutionError): string {
    let errorMessage = '';
    const field: IAttribute = resolutionError.details.field;
    const expressionType: ExpressionType = resolutionError.details.expressionType === 'Array' && resolutionError.details.expressionSubType
        ? `Array[${resolutionError.details.expressionSubType}]` : resolutionError.details.expressionType;
    errorMessage = format(stringResources.Expression_resolution_TypeMismatch, expressionType, field.name);
    let acceptTypes: Array<string | ExpressionType> = getAcceptsByConfig(field, resolutionError.details.entity as Owned);
    if (arrayIsNullOrEmpty(acceptTypes)) {
        // if not configed accpets, will try to give 1 suggest type
        acceptTypes = [suggestValidType(field)];
    }
    if (Array.isArray(acceptTypes)) {
        if (acceptTypes.length === 1) {
            errorMessage = format(stringResources.Expression_resolution_ExpectedSingleType, errorMessage, acceptTypes[0]);
        } else if (acceptTypes.length > 1) {
            const lastType = acceptTypes.pop();
            const preTypes = acceptTypes.join('\', \'');
            errorMessage = format(stringResources.Expression_resolution_ExpectedMultiTypes, errorMessage, preTypes, lastType);
        }
    }
    return errorMessage;
}

